import { useQuery } from '@tanstack/react-query'
import type {
  CompanyInfo,
  Cases,
  News,
  Disclosures,
  WebsiteHomeContent,
  WebsitePublicNotice,
  WebsitePublicNoticeAttachment,
  WebsiteCertificate,
  WebsiteRecruitPosition,
  WebsitePublicNoticeOverview,
  ApiResponse,
} from '@/types'

// 获取公司信息
export const useCompanyData = () => {
  return useQuery<CompanyInfo>({
    queryKey: ['company'],
    queryFn: () => fetch('/src/data/company.json').then(res => res.json())
  })
}

// 获取资质证书
export const useCertificates = () => {
  return useQuery<WebsiteCertificate[]>({
    queryKey: ['certificates'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/certificates')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useCertificates] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteCertificate[]).filter((item) => item)
    },
    staleTime: 1000 * 60,
  })
}

// 获取工程案例
export const useCases = (category?: string) => {
  return useQuery<Cases>({
    queryKey: ['cases', category],
    queryFn: () => fetch('/src/data/cases.json').then(res => res.json())
  })
}

// 获取新闻资讯
export const useNews = (category?: string) => {
  return useQuery<News>({
    queryKey: ['news', category],
    queryFn: () => fetch('/src/data/news.json').then(res => res.json())
  })
}

// 获取信息公示概览
export const usePublicNoticeOverview = () => {
  return useQuery<WebsitePublicNoticeOverview>({
    queryKey: ['public-notice-overview'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/public/notice/overview')
      if (!payload || !isApiSuccess(payload) || !payload.data) {
        console.warn('[usePublicNoticeOverview] 接口返回异常或数据格式不正确', payload)
        return {
          categories: [],
          statistics: { total: 0, yearly: 0, updated: '' },
          items: [],
        }
      }

      const overview = payload.data as WebsitePublicNoticeOverview
      return {
        categories: overview.categories ?? [],
        statistics: overview.statistics ?? { total: 0, yearly: 0, updated: '' },
        items: (overview.items ?? []).map((item) => ({
          ...item,
          attachments: item.attachments ?? [],
        })),
      }
    },
    staleTime: 1000 * 60,
  })
}

// 获取招聘岗位
export const useRecruitPositions = () => {
  return useQuery<WebsiteRecruitPosition[]>({
    queryKey: ['recruit-positions'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/recruit/positions')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useRecruitPositions] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteRecruitPosition[]).filter((item) => item)
    },
    staleTime: 1000 * 60,
  })
}

const fetchJson = async <T>(input: RequestInfo | URL, init?: RequestInit): Promise<T | null> => {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch (error) {
    console.warn('[fetchJson] 请求失败:', input, error)
    return null
  }

  if (!response.ok) {
    console.warn('[fetchJson] 请求返回非 2xx 状态:', input, response.status)
    return null
  }

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.toLowerCase().includes('application/json')) {
    console.warn('[fetchJson] 请求返回的 Content-Type 非 JSON:', input, contentType)
    return null
  }

  try {
    return await response.json()
  } catch (error) {
    console.warn('[fetchJson] 解析 JSON 失败:', input, error)
    return null
  }
}

const isApiSuccess = (payload: any) => {
  if (!payload || typeof payload !== 'object') return false
  const code = payload.code ?? payload.errCode
  return code !== undefined && Number(code) === 0
}

export const useHeroBanners = () => {
  return useQuery<WebsiteHomeContent[]>({
    queryKey: ['hero-banners'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/banners')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useHeroBanners] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteHomeContent[]).filter((item) => item && item.status === 'enabled')
    },
    staleTime: 1000 * 60,
  })
}

export const useMiniBanners = () => {
  return useQuery<WebsiteHomeContent[]>({
    queryKey: ['mini-banners'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/mini/banners')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useMiniBanners] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteHomeContent[]).filter((item) => item && item.status === 'enabled')
    },
    staleTime: 1000 * 60,
  })
}

export const useIndustryTrends = () => {
  return useQuery<WebsiteHomeContent[]>({
    queryKey: ['industry-trends'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/industry/news')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useIndustryTrends] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteHomeContent[]).filter((item) => item && item.status === 'enabled')
    },
    staleTime: 1000 * 60,
  })
}

export const useHomeNotices = () => {
  return useQuery<WebsiteHomeContent[]>({
    queryKey: ['home-notices'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/home/notices')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[useHomeNotices] 接口返回异常或数据格式不正确', payload)
        return []
      }

      return (payload.data as WebsiteHomeContent[]).filter((item) => item && item.status === 'enabled')
    },
    staleTime: 1000 * 60,
  })
}

const parseNoticeAttachments = (attachmentsJson?: string): WebsitePublicNoticeAttachment[] => {
  if (!attachmentsJson) return []
  try {
    const parsed = JSON.parse(attachmentsJson)
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          name: String(item.name ?? item.fileName ?? '附件'),
          url: String(item.url ?? item.fileUrl ?? '#'),
        }))
    }
  } catch (error) {
    console.warn('[parseNoticeAttachments] 解析失败', error)
  }
  return []
}

export const usePublicNotices = () => {
  return useQuery<WebsitePublicNotice[]>({
    queryKey: ['public-notices'],
    queryFn: async () => {
      const payload = await fetchJson<any>('/api/v1/public/website/public/notices')
      if (!payload || !isApiSuccess(payload) || !Array.isArray(payload.data)) {
        console.warn('[usePublicNotices] 接口返回异常或数据格式不正确', payload)
        return []
      }
      return (payload.data as WebsitePublicNotice[])
        .filter((item) => item)
        .map((item) => ({
          ...item,
          attachments: parseNoticeAttachments(item.attachmentsJson),
        }))
    },
    staleTime: 1000 * 60,
  })
} 